import { Box, useLayoutContext } from "arkh";
import { FlatList, Platform } from "react-native";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";

import { graphql, useFragment, useRelayEnvironment } from "react-relay";

import { RoutineOccasionScreenRoutineDescription } from "./RoutineOccasionScreenRoutineDescription";
import { RoutineOccasionScreenListItem } from "./RoutineOccasionScreenListItem";
import { OccasionSelector } from "./OccasionSelector";
import { NavProp } from "./RoutineOccasionScreen";
import {
  RoutineOccasionList_Routine$data,
  RoutineOccasionList_Routine$key,
} from "./__generated__/RoutineOccasionList_Routine.graphql";

import { useReportIssueBottomSheetMainScreenFirstSnapPointHeight } from "../../ReportIssueBottomSheet/useReportIssueBottomSheetMainScreenFirstSnapPoint";
import { FlashList } from "@shopify/flash-list";
import { RoutineOccasionSectionHeader } from "./RoutineOccasionSectionHeader";
import { RoutineOccasionTodoIssue } from "./RoutineOccasionTodoIssue";
import {
  HasPermission,
  useHasPermissions,
} from "@/src/OS/Login/usePermissions";
import { OutlinedHeaderButton } from "./OutlinedHeaderButton";

import { RoutineOccasionAssignees } from "./RoutineOccasionAssignees";
import { RoutineOccasionEditAssigneesPopover } from "./RoutineOccasionEditAssigneesPopover";
import { KeyboardAwareFlashListScrollComponent } from "@/src/components/mobile/KeyboardAwareFlashListScrollComponent";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { Features, useHasFeatureInTier } from "@/src/useHasFeatureInTier";

export const RoutineOccasionList = React.memo(RoutineOccasionList_);

type Props = {
  navigation: NavProp;
  isArchived: boolean;
  sequenceNumber: number;
  routineKey: RoutineOccasionList_Routine$key;
  onRequestBottomSheet: () => void;
  presentTodo: (newlyCreated: boolean, todoId: string) => void;
  createIssue: (taskExecutionId: string, taskTitle: string) => void;
  taskId?: string;
  storeId?: string;
};

function RoutineOccasionList_(props: Props) {
  const routine = useFragment(
    graphql`
      fragment RoutineOccasionList_Routine on Routine {
        id
        isOrg
        title
        isArchived
        ...RoutineOccasionScreenRoutineDescription_Routine
        occasion(sequenceNumber: $sequenceNumber, storeId: $storeId)
          @required(action: NONE) {
          store {
            myRole
            ...usePermissions_store
            ...useHasFeatureInTier_Store
          }
          ...RoutineOccasionAssignees_RoutineOccasion
          ...OccasionSelector_routineOccasion
          occasionSections @required(action: NONE) {
            id

            ...RoutineOccasionSectionHeader_routineOccasionSection
            sectionVersion {
              title
            }
            taskExecutions {
              id
              task {
                id
              }

              todos {
                id
                ...RoutineOccasionTodoIssue_todo
              }
              ...RoutineOccasionScreenListItemComponent_TaskExecution
            }
          }
        }
      }
    `,
    props.routineKey
  );

  useEffect(() => {
    if (!routine?.id) {
      props.navigation.goBack();
    }
  }, [routine, props.navigation]);

  if (!routine?.occasion?.store) {
    return null;
  }

  return <RoutineOccasionList__ routine={routine} {...props} />;
}

function RoutineOccasionList__({
  sequenceNumber,
  navigation,
  onRequestBottomSheet,
  taskId,
  routine,
  presentTodo,
  createIssue,
  storeId,
}: Props & { routine: NonNullable<RoutineOccasionList_Routine$data> }) {
  const [assigneesPopoverVisible, setAssigneesPopoverVisible] = useState(false);
  const issueBottomSheetHeight =
    useReportIssueBottomSheetMainScreenFirstSnapPointHeight();
  const hasRoutineAdmin = useHasPermissions(
    routine.occasion.store,
    "ROUTINE_ADMIN"
  );
  const layout = useLayoutContext();

  const ListHeaderComponent = useMemo(
    () => (
      <RoutineOccasionScreenRoutineDescription
        routine={routine}
        hasRoutineAdmin={hasRoutineAdmin}
        onCustomise={() => {
          navigation.navigate("RoutineStoreCustomize", {
            routineId: routine.id,
            storeId,
          });
        }}
      >
        <RoutineOccasionAssignees
          occasion={routine.occasion}
          onPress={() => setAssigneesPopoverVisible(true)}
        />
      </RoutineOccasionScreenRoutineDescription>
    ),
    [routine, hasRoutineAdmin, navigation, sequenceNumber, storeId]
  );

  const hasHistory = useHasFeatureInTier(
    Features.FOLLOWUP_AND_HISTORY,
    routine.occasion.store!
  );

  const isOrg = routine.isOrg ?? false;

  useLayoutEffect(() => {
    navigation.setOptions({
      headerTitleAlign: "center",
      headerTitle: () => (
        <OccasionSelector
          occasion={routine.occasion}
          historyEnabled={hasHistory}
          onPress={onRequestBottomSheet}
        />
      ),
      headerRight: () => (
        <Box style={{ flexDirection: "row", alignItems: "center" }}>
          {routine.occasion.store ? (
            <HasPermission
              store={routine.occasion.store}
              permission="ROUTINE_ADMIN"
            >
              {isOrg ? null : (
                <OutlinedHeaderButton
                  onPress={() => {
                    navigation.navigate("RoutineEdit", {
                      id: routine.id,
                    });
                  }}
                  containerStyle={{
                    paddingLeft: layout.space.small,
                    paddingRight:
                      Platform.OS === "web" ? layout.space.medium : 0,
                  }}
                  title="Admin"
                />
              )}
            </HasPermission>
          ) : null}
          {hasHistory && Platform.OS !== "web" ? (
            <OutlinedHeaderButton
              containerStyle={{
                paddingLeft: layout.space.small,
              }}
              hitSlop={{ right: layout.space.medium }}
              pressRetentionOffset={{
                right: layout.space.medium,
              }}
              onPress={() =>
                navigation.navigate("RoutineDetail", {
                  screen: "RoutineDetailMain",
                  params: {
                    routineId: routine.id,
                    routineTitle: routine.title,
                  },
                })
              }
              icon="analytics"
            />
          ) : null}
        </Box>
      ),
    });
  }, [
    hasHistory,
    isOrg,
    layout.space.medium,
    layout.space.small,
    navigation,
    onRequestBottomSheet,
    routine.id,
    routine.occasion,
    routine.title,
  ]);

  const listData = useMemo(() => {
    return routine.occasion.occasionSections.flatMap(
      (section, sectionIndex) => {
        const taskExecutions = (section?.taskExecutions ?? []).flatMap((te) => {
          if (taskId) {
            if (te.task.id !== taskId) {
              return [];
            }
          }
          const todos =
            te?.todos?.flatMap((todo, todoIndex) => {
              if (!todo) {
                return [];
              }

              const _id = te.id + todoIndex;
              return [
                {
                  type: "todo" as const,
                  todo,
                  id: _id,
                  isFirst: todoIndex === 0,
                  isLast: todoIndex === te.todos!.length - 1,
                },
              ];
            }) ?? [];

          return [
            {
              type: "taskExecution" as const,
              taskExecution: te,
              sectionIndex,
              id: te.id,
            },

            ...todos,
          ];
        });
        if (taskExecutions.length === 0) {
          return [];
        }
        return [
          { type: "section" as const, section, id: section.id },
          ...taskExecutions,
          {
            id: `sectionFooter${sectionIndex}`,
            type: "sectionFooter" as const,
          },
        ];
      }
    );
  }, [routine.occasion.occasionSections, taskId]);

  const onPresentTodo = useCallback(
    (todoId: string) => {
      presentTodo(false, todoId);
    },
    [presentTodo]
  );
  const renderItem = useCallback(
    ({ item }: { item: (typeof listData)[number]; index: number }) => {
      switch (item.type) {
        case "section":
          return (
            <RoutineOccasionSectionHeader
              routineOccasionSection={item.section}
            />
          );
        case "sectionFooter":
          return <Box spaceBottom="medium" />;
        case "taskExecution": {
          const issueLenth = item.taskExecution.todos?.length ?? 0;
          return (
            <Box spaceBottom={issueLenth === 0 ? "medium" : "none"}>
              <RoutineOccasionScreenListItem
                navigation={navigation}
                taskExecution={item.taskExecution}
                createIssue={createIssue}
                isArchived={routine.isArchived ?? false}
                readOnly={routine.occasion.store?.myRole === "READ_ONLY"}
              />
            </Box>
          );
        }

        case "todo":
          return (
            <RoutineOccasionTodoIssue
              isFirst={item.isFirst}
              isLast={item.isLast}
              todo={item.todo}
              disabled={routine.isArchived ?? false}
              openTodo={onPresentTodo}
            />
          );
      }
    },
    [
      createIssue,
      navigation,
      onPresentTodo,
      routine.isArchived,
      routine.occasion.store?.myRole,
    ]
  );

  const env = useRelayEnvironment();
  React.useEffect(() => {
    const cb = () => {
      env.commitUpdate((store) => {
        const s = store.getRoot()?.getLinkedRecord("store");

        const currentId = s?.getValue("activeIssueId");
        if (currentId) {
          s?.setValue(null, "activeIssueId");
        }
      });
    };
    navigation.addListener("beforeRemove", cb);
    return () => navigation.removeListener("beforeRemove", cb);
  }, [env, navigation]);

  const insets = useSafeAreaInsets();

  const assigneesPopover = (
    <RoutineOccasionEditAssigneesPopover
      routineId={routine.id}
      sequenceNumber={sequenceNumber}
      storeId={storeId}
      visible={assigneesPopoverVisible}
      onClose={() => setAssigneesPopoverVisible(false)}
      disabled={routine.occasion.store?.myRole === "READ_ONLY"}
    />
  );

  if (Platform.OS === "web") {
    return (
      <>
        <FlatList
          data={listData}
          ListHeaderComponent={ListHeaderComponent}
          style={{ flex: 1 }}
          contentContainerStyle={{
            paddingTop: layout.space.medium,
            paddingBottom: issueBottomSheetHeight + layout.space.medium,
          }}
          scrollIndicatorInsets={{
            bottom: issueBottomSheetHeight,
          }}
          keyExtractor={keyExtractor}
          renderItem={renderItem}
          keyboardDismissMode="on-drag"
        />
        {assigneesPopover}
      </>
    );
  }

  return (
    <>
      <FlashList
        ListHeaderComponent={ListHeaderComponent}
        contentContainerStyle={{
          paddingTop: layout.space.medium,
          paddingBottom:
            issueBottomSheetHeight + layout.space.medium + insets.bottom,
        }}
        scrollIndicatorInsets={{
          bottom: issueBottomSheetHeight,
        }}
        data={listData}
        keyExtractor={keyExtractor}
        getItemType={getItemType}
        renderItem={renderItem}
        keyboardDismissMode={Platform.OS === "ios" ? "interactive" : "on-drag"}
        renderScrollComponent={KeyboardAwareFlashListScrollComponent}
      />
      {assigneesPopover}
    </>
  );
}

function keyExtractor(item: { id: string }) {
  return item.id;
}
function getItemType(item: { type: string }) {
  return item.type;
}
